// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { Resources } from '@shared/localizations/Resources';
import { DrawPointer, DrawPointerTypeEnum } from '../Utils/DrawPointer';
import { HistoryType } from '@shared/utils/History/HistoryType';
import { TerceraChartBaseRenderer } from './TerceraChartBaseRenderer';
import { TerceraChartCashItemSeriesDataType } from '../Series/TerceraChartCashItemSeriesEnums';
import { Color, Pen, SolidBrush } from '@shared/commons/Graphics';
import { TerceraChartAction, TerceraChartActionEnum } from '../TerceraChartAction';
import { ThemeManager } from '@front/controls/misc/ThemeManager';
import { Periods } from '@shared/utils/History/TFInfo';
import { DynProperty } from '@shared/commons/DynProperty';
import { getTerceraChartSpreadTypeKey, TerceraChartSpreadType } from '@shared/utils/History/SpreadType';
import { DataCache } from '@shared/commons/DataCache';
import { LayersEnum } from '../Utils/LayersEnum';
import { createContextMenuItemsFromEnum } from '@shared/utils/ContextMenuUtils';
export class TerceraChartSpreadRenderer extends TerceraChartBaseRenderer {
    constructor(chart) {
        super(chart);
        this.spreadBrush = new SolidBrush('');
        this.askPen = new Pen('');
        this.askBrush = new SolidBrush('');
        this.bidPen = new Pen('');
        this.bidBrush = new SolidBrush('');
        this.lastPen = new Pen('');
        this.lastBrush = new SolidBrush('');
        this.PriceScaleFont = ThemeManager.Fonts.Font_12F_regular.copy();
        this.PriceIndicatorDrawingType = TercaraChartPriceIndicatorType.ScaleMarkerWithLine;
        this.TerceraChartSpreadType = TerceraChartSpreadType.None;
        this.withAsset = false;
        this.percentView = false;
        this.Localize();
        this.assignLayer = LayersEnum.Quotes;
        this.SetClassName('TerceraChartSpreadRenderer');
        this.Instrument = chart.Instrument();
    }
    get Instrument() {
        return this._instrument;
    }
    set Instrument(value) {
        const wasSubscribed = this.shouldBeSubscribed();
        if (wasSubscribed) {
            this.unsubscribeToQuote();
        }
        this._instrument = value;
        const shouldBeSubscribedNow = this.shouldBeSubscribed();
        if (shouldBeSubscribedNow) {
            this.subscribeToQuote();
        }
    }
    get PriceIndicatorDrawingType() {
        return this._priceIndicatorDrawingType;
    }
    set PriceIndicatorDrawingType(value) {
        this._priceIndicatorDrawingType = value;
        this.Visible = value !== TercaraChartPriceIndicatorType.None;
    }
    get LastPrice_BidIndicatorColor() {
        return this.lastPrice_BidIndicatorColor;
    }
    set LastPrice_BidIndicatorColor(value) {
        this.lastPrice_BidIndicatorColor = value;
        if (this.bidBrush)
            this.bidBrush.Color = this.lastPrice_BidIndicatorColor;
        if (this.bidPen)
            this.bidPen.Color = this.lastPrice_BidIndicatorColor;
    }
    get LastPrice_AskIndicatorColor() {
        return this.lastPrice_AskIndicatorColor;
    }
    set LastPrice_AskIndicatorColor(value) {
        this.lastPrice_AskIndicatorColor = value;
        if (this.askBrush)
            this.askBrush.Color = this.lastPrice_AskIndicatorColor;
        if (this.askPen)
            this.askPen.Color = this.lastPrice_AskIndicatorColor;
    }
    get LastPrice_LastIndicatorColor() {
        return this.lastPrice_LastIndicatorColor;
    }
    set LastPrice_LastIndicatorColor(value) {
        this.lastPrice_LastIndicatorColor = value;
        if (this.lastBrush)
            this.lastBrush.Color = this.lastPrice_LastIndicatorColor;
        if (this.lastPen)
            this.lastPen.Color = this.lastPrice_LastIndicatorColor;
    }
    get LastPrice_SpreadIndicatorColor() {
        return this.lastPrice_SpreadIndicatorColor;
    }
    set LastPrice_SpreadIndicatorColor(value) {
        this.lastPrice_SpreadIndicatorColor = value;
        if (this.spreadBrush)
            this.spreadBrush.Color = this.lastPrice_SpreadIndicatorColor;
    }
    Dispose() {
        if (this.shouldBeSubscribed()) {
            this.unsubscribeToQuote();
        }
        super.Dispose();
    }
    Draw(gr, window, windowsContainer, advParams) {
        if (!this.Visible) {
            return;
        }
        const p = advParams;
        if (!p)
            return;
        const instrument = p.Instrument;
        const acc = p.Account;
        if (!instrument || !p.mainPriceRenderer.Series) {
            return;
        }
        const cashItemSeries = p.mainPriceRenderer.Series;
        const tradingDataBid = instrument.Level1.GetBid(acc);
        const tradingDataAsk = instrument.Level1.GetAsk(acc);
        let tradingDataLastPrice = NaN;
        const cashItem = cashItemSeries.CashItem;
        if (cashItem.HistoryType === HistoryType.LAST && cashItem.Count() > 0) {
            tradingDataLastPrice = cashItem.getLastPrice();
        }
        else {
            tradingDataLastPrice = instrument.Level1.GetLastPrice(acc);
        }
        const dataType = this.chart.cashItemSeriesSettings.DataType;
        const lastValueClipRect = this.chart.mainWindow.rightYScaleRenderer.Rectangle;
        const mainClipRect = window.ClientRectangle;
        const mainClipRectR = mainClipRect.X + mainClipRect.Width;
        gr.save();
        gr.beginPath();
        gr.rect(mainClipRect.X, mainClipRect.Y, lastValueClipRect.X + lastValueClipRect.Width - mainClipRect.X, lastValueClipRect.Y + lastValueClipRect.Height - mainClipRect.Y);
        gr.clip();
        if (this.PriceIndicatorDrawingType !== TercaraChartPriceIndicatorType.None) {
            const tickPeriod = cashItemSeries.CashItem.FPeriod === Periods.TIC;
            const bidPrice = cashItemSeries.CashItem.HistoryType !== HistoryType.ASK;
            const tradePrice = cashItemSeries.CashItem.HistoryType === HistoryType.LAST;
            let curPriceValue = 0;
            let invertCurPriceValue = 0;
            let spreadBid = 0;
            let spreadAsk = 0;
            let spreadLast = 0;
            let spreadBidY = 0;
            let spreadAskY = 0;
            let spreadLastY = 0;
            const pc = window.PointsConverter;
            let spreadlastFormated = '';
            let formatcurPriceValue = '';
            let invertFormatcurPriceValue = '';
            spreadBid = tradingDataBid;
            spreadAsk = tradingDataAsk;
            spreadLast = tradingDataLastPrice;
            switch (dataType) {
                case TerceraChartCashItemSeriesDataType.Absolute:
                    spreadBidY = pc.GetScreenY(spreadBid);
                    spreadAskY = pc.GetScreenY(spreadAsk);
                    spreadLastY = pc.GetScreenY(spreadLast);
                    spreadlastFormated =
                        this.withAsset && acc ? acc.formatPrice(spreadLast) : instrument.formatPrice(spreadLast);
                    curPriceValue = bidPrice ? spreadBid : spreadAsk;
                    invertCurPriceValue = bidPrice ? spreadAsk : spreadBid;
                    formatcurPriceValue = instrument.formatPrice(curPriceValue);
                    invertFormatcurPriceValue = instrument.formatPrice(invertCurPriceValue);
                    break;
                case TerceraChartCashItemSeriesDataType.Relative:
                    spreadBid = cashItemSeries.settings.relativeDataConverter.Calculate(spreadBid);
                    spreadAsk = cashItemSeries.settings.relativeDataConverter.Calculate(spreadAsk);
                    spreadBidY = pc.GetScreenY(spreadBid);
                    spreadAskY = pc.GetScreenY(spreadAsk);
                    spreadLast = cashItemSeries.settings.relativeDataConverter.Calculate(spreadLast);
                    spreadLastY = pc.GetScreenY(spreadLast);
                    spreadlastFormated = spreadLast.toFixed(2) + '%';
                    curPriceValue = bidPrice ? spreadBid : spreadAsk;
                    invertCurPriceValue = bidPrice ? spreadAsk : spreadBid;
                    formatcurPriceValue = curPriceValue.toFixed(2) + '%';
                    invertFormatcurPriceValue = invertCurPriceValue.toFixed(2) + '%';
                    break;
                case TerceraChartCashItemSeriesDataType.Log:
                    spreadBid = cashItemSeries.settings.logDataConverter.Calculate(spreadBid);
                    spreadAsk = cashItemSeries.settings.logDataConverter.Calculate(spreadAsk);
                    spreadBidY = pc.GetScreenY(spreadBid);
                    spreadAskY = pc.GetScreenY(spreadAsk);
                    spreadLast = cashItemSeries.settings.logDataConverter.Calculate(spreadLast);
                    spreadLastY = pc.GetScreenY(spreadLast);
                    spreadlastFormated =
                        this.withAsset && acc
                            ? acc.formatPrice(cashItemSeries.settings.logDataConverter.Revert(spreadLast))
                            : instrument.formatPrice(cashItemSeries.settings.logDataConverter.Revert(spreadLast));
                    curPriceValue = bidPrice ? spreadBid : spreadAsk;
                    invertCurPriceValue = bidPrice ? spreadAsk : spreadBid;
                    formatcurPriceValue = instrument.formatPrice(cashItemSeries.settings.logDataConverter.Revert(curPriceValue));
                    invertFormatcurPriceValue = instrument.formatPrice(cashItemSeries.settings.logDataConverter.Revert(invertCurPriceValue));
                    break;
            }
            if (!tradePrice) {
                if (tickPeriod) {
                    p.DrawPointers.push(new DrawPointer(DrawPointerTypeEnum.BidAsk, curPriceValue, bidPrice ? this.bidBrush : this.askBrush, formatcurPriceValue));
                    p.DrawPointers.push(new DrawPointer(DrawPointerTypeEnum.BidAsk, invertCurPriceValue, bidPrice ? this.askBrush : this.bidBrush, invertFormatcurPriceValue));
                }
                else {
                    if (!isNaN(curPriceValue)) {
                        p.DrawPointers.push(new DrawPointer(DrawPointerTypeEnum.BidAsk, curPriceValue, bidPrice ? this.bidBrush : this.askBrush, formatcurPriceValue));
                    }
                }
            }
            else {
                curPriceValue = spreadLast;
                if (this.PriceIndicatorDrawingType === TercaraChartPriceIndicatorType.ScaleMarkerWithLine) {
                    gr.DrawLine(this.lastPen, mainClipRect.X + 2, spreadLastY, mainClipRectR - 1, spreadLastY);
                }
                p.DrawPointers.push(new DrawPointer(DrawPointerTypeEnum.BidAsk, spreadLast, this.lastBrush, spreadlastFormated));
            }
            if (!tickPeriod) {
                let bidLineIsAlreadyDrawn = false;
                let askLineIsAlreadyDrawn = false;
                if (!tradePrice &&
                    this.PriceIndicatorDrawingType === TercaraChartPriceIndicatorType.ScaleMarkerWithLine) {
                    if (bidPrice && !isNaN(spreadBid)) {
                        gr.DrawLine(this.bidPen, mainClipRect.X + 2, spreadBidY, mainClipRectR - 1, spreadBidY);
                        bidLineIsAlreadyDrawn = true;
                    }
                    else if (!isNaN(spreadAsk)) {
                        gr.DrawLine(this.askPen, mainClipRect.X + 2, spreadAskY, mainClipRectR - 1, spreadAskY);
                        askLineIsAlreadyDrawn = true;
                    }
                }
                if (this.TerceraChartSpreadType == TerceraChartSpreadType.LinesWithPrices ||
                    this.TerceraChartSpreadType == TerceraChartSpreadType.Lines) {
                    if (!bidLineIsAlreadyDrawn && !isNaN(spreadBid)) {
                        gr.DrawLine(this.bidPen, mainClipRect.X + 2, spreadBidY, mainClipRectR - 1, spreadBidY);
                    }
                    if (!askLineIsAlreadyDrawn && !isNaN(spreadAsk)) {
                        gr.DrawLine(this.askPen, mainClipRect.X + 2, spreadAskY, mainClipRectR - 1, spreadAskY);
                    }
                }
                if (this.TerceraChartSpreadType === TerceraChartSpreadType.Area &&
                    !isNaN(spreadBid) &&
                    !isNaN(spreadAsk)) {
                    if (spreadAskY > spreadBidY) {
                        gr.FillRect(this.SpreadIncorrectBackBrush, mainClipRect.X + 1, spreadBidY, mainClipRect.Width - 1, spreadAskY - spreadBidY);
                    }
                    else {
                        gr.FillRect(this.spreadBrush, mainClipRect.X + 1, spreadAskY, mainClipRect.Width - 1, spreadBidY - spreadAskY);
                    }
                }
                if (this.TerceraChartSpreadType === TerceraChartSpreadType.LinesWithPrices) {
                    let priceText = invertFormatcurPriceValue;
                    const PriceScaleFont = this.PriceScaleFont;
                    const askBrush = this.askBrush;
                    const bidBrush = this.bidBrush;
                    const value = bidPrice ? spreadAsk : spreadBid;
                    if (!isNaN(value)) {
                        gr.DrawString(priceText, PriceScaleFont, bidPrice ? askBrush : bidBrush, mainClipRectR - gr.GetTextWidth(priceText, PriceScaleFont), (bidPrice ? spreadAskY : spreadBidY) + (bidPrice ? -(PriceScaleFont.Height + 2) : 2));
                    }
                    if (tradePrice) {
                        priceText = formatcurPriceValue;
                        gr.DrawString(priceText, PriceScaleFont, bidPrice ? bidBrush : askBrush, mainClipRectR - gr.GetTextWidth(priceText, PriceScaleFont), (bidPrice ? spreadBidY : spreadAskY) + (bidPrice ? 2 : -(PriceScaleFont.Height + 2)));
                    }
                }
            }
        }
        gr.restore();
    }
    ThemeChanged() {
        this.SpreadIncorrectBackBrush = new SolidBrush(ThemeManager.colors.Chart_SpreadIncorrectBackColor);
        this.LastPrice_BidIndicatorColor = ThemeManager.colors.chartBuyColor;
        this.LastPrice_AskIndicatorColor = ThemeManager.colors.chartSellColor;
        this.LastPrice_LastIndicatorColor = ThemeManager.colors.chartAccentColor;
        this.LastPrice_SpreadIndicatorColor = Color.FromArgb(40, ThemeManager.colors.Chart_TimeScaleCursorBackColor);
    }
    Properties() {
        const properties = [];
        let prop = null;
        let SeparatorGroup = null;
        const state = this.chart.TerceraChartActionProcessor.GetTerceraChartActionState(TerceraChartAction.Create(TerceraChartActionEnum.PriceIndicator));
        if (!state.Visible) {
            return properties;
        }
        SeparatorGroup = '#4#' + Resources.getResource('property.SeparatorGroup.HighlightPrices');
        prop = new DynProperty('PriceIndicatorDrawingType', this.PriceIndicatorDrawingType, DynProperty.COMBOBOX, DynProperty.PRICE_SCALE_GROUP);
        prop.sortIndex = 10;
        prop.objectVariants = createContextMenuItemsFromEnum(TercaraChartPriceIndicatorType, getTercaraChartPriceIndicatorTypeKey);
        prop.separatorGroup = SeparatorGroup;
        properties.push(prop);
        prop = new DynProperty('lastPrice_BidIndicatorColor', this.LastPrice_BidIndicatorColor, DynProperty.COLOR, DynProperty.PRICE_SCALE_GROUP);
        prop.separatorGroup = SeparatorGroup;
        prop.sortIndex = 40;
        properties.push(prop);
        prop = new DynProperty('lastPrice_AskIndicatorColor', this.LastPrice_AskIndicatorColor, DynProperty.COLOR, DynProperty.PRICE_SCALE_GROUP);
        prop.separatorGroup = SeparatorGroup;
        prop.sortIndex = 20;
        properties.push(prop);
        prop = new DynProperty('lastPrice_LastIndicatorColor', this.LastPrice_LastIndicatorColor, DynProperty.COLOR, DynProperty.PRICE_SCALE_GROUP);
        prop.separatorGroup = SeparatorGroup;
        prop.sortIndex = 30;
        properties.push(prop);
        prop = new DynProperty('lastPrice_SpreadIndicatorColor', this.LastPrice_SpreadIndicatorColor, DynProperty.COLOR, DynProperty.PRICE_SCALE_GROUP);
        prop.separatorGroup = SeparatorGroup;
        prop.enabilityValue = TerceraChartSpreadType.Area;
        prop.enabled = this.TerceraChartSpreadType !== TerceraChartSpreadType.None;
        prop.sortIndex = 44;
        properties.push(prop);
        prop = new DynProperty('SpreadType', this.TerceraChartSpreadType, DynProperty.COMBOBOX, DynProperty.PRICE_SCALE_GROUP);
        prop.sortIndex = 42;
        prop.assignedProperty = ['lastPrice_SpreadIndicatorColor'];
        prop.objectVariants = createContextMenuItemsFromEnum(TerceraChartSpreadType, getTerceraChartSpreadTypeKey);
        prop.separatorGroup = SeparatorGroup;
        properties.push(prop);
        return properties;
    }
    callBack(properties) {
        // Apply property values from the provided properties array
        const wasSubscribed = this.shouldBeSubscribed();
        let dp = DynProperty.getPropertyByName(properties, 'PriceIndicatorDrawingType');
        if (dp)
            this.PriceIndicatorDrawingType = dp.value;
        dp = DynProperty.getPropertyByName(properties, 'SpreadType');
        if (dp)
            this.TerceraChartSpreadType = dp.value;
        dp = DynProperty.getPropertyByName(properties, 'lastPrice_BidIndicatorColor');
        if (dp)
            this.LastPrice_BidIndicatorColor = dp.value;
        dp = DynProperty.getPropertyByName(properties, 'lastPrice_AskIndicatorColor');
        if (dp)
            this.LastPrice_AskIndicatorColor = dp.value;
        dp = DynProperty.getPropertyByName(properties, 'lastPrice_LastIndicatorColor');
        if (dp)
            this.LastPrice_LastIndicatorColor = dp.value;
        dp = DynProperty.getPropertyByName(properties, 'lastPrice_SpreadIndicatorColor');
        if (dp)
            this.LastPrice_SpreadIndicatorColor = dp.value;
        const shouldBeSubscribedNow = this.shouldBeSubscribed();
        if (wasSubscribed && !shouldBeSubscribedNow) {
            this.unsubscribeToQuote();
        }
        else if (!wasSubscribed && shouldBeSubscribedNow) {
            this.subscribeToQuote();
        }
    }
    newQuote() { }
    shouldBeSubscribed() {
        return (!isNullOrUndefined(this.Instrument) &&
            this.Visible &&
            this.TerceraChartSpreadType !== TerceraChartSpreadType.None);
    }
    subscribeToQuote() {
        DataCache.FQuoteCache.addListener(this.Instrument, this, HistoryType.QUOTE_LEVEL1);
    }
    unsubscribeToQuote() {
        DataCache.FQuoteCache.removeListener(this.Instrument, this, HistoryType.QUOTE_LEVEL1);
    }
}
// SPREAD INDICATOR
export var TercaraChartPriceIndicatorType;
(function (TercaraChartPriceIndicatorType) {
    TercaraChartPriceIndicatorType[TercaraChartPriceIndicatorType["ScaleMarker"] = 0] = "ScaleMarker";
    TercaraChartPriceIndicatorType[TercaraChartPriceIndicatorType["ScaleMarkerWithLine"] = 1] = "ScaleMarkerWithLine";
    TercaraChartPriceIndicatorType[TercaraChartPriceIndicatorType["None"] = 2] = "None";
})(TercaraChartPriceIndicatorType || (TercaraChartPriceIndicatorType = {}));
export function getTercaraChartPriceIndicatorTypeKey(value) {
    switch (value) {
        case TercaraChartPriceIndicatorType.ScaleMarker:
            return 'property.PriceIndicatorDrawingType.ScaleMarker';
        case TercaraChartPriceIndicatorType.ScaleMarkerWithLine:
            return 'property.PriceIndicatorDrawingType.ScaleMarkerWithLine';
        case TercaraChartPriceIndicatorType.None:
            return 'property.PriceIndicatorDrawingType.None';
        default:
            return '';
    }
}
